/**
 * Persistent Cache Service
 * 持久化缓存服务 - 基于 LocalStorage 的 L2 缓存
 * 
 * 职责：
 * 1. 缓存公共数据（食谱、食材）到 LocalStorage
 * 2. 缓存用户数据（短期）到 LocalStorage
 * 3. 提供缓存失效和更新机制
 * 4. 支持缓存版本控制
 * 
 * 缓存策略：
 * - 公共食谱：30 分钟 TTL，版本控制
 * - 公共食材：30 分钟 TTL，版本控制
 * - 用户数据：5 分钟 TTL
 * - 元数据：永久（直到手动清除）
 */

import { LRUCache, TTL_PRESETS } from './lruCache';

export interface PersistentCacheOptions {
    /** 是否启用 */
    enabled?: boolean;
    /** 是否启用日志 */
    debug?: boolean;
}

export interface CacheMetadata {
    /** 缓存时间戳 */
    timestamp: number;
    /** 过期时间戳 */
    expiresAt: number;
    /** 数据版本 */
    version?: string;
    /** 数据大小（字节） */
    size?: number;
}

export interface CachedData<T> {
    /** 元数据 */
    metadata: CacheMetadata;
    /** 缓存的数据 */
    data: T;
}

export class PersistentCacheService {
    private cache: LRUCache<string, CachedData<any>>;
    private enabled: boolean;
    private debug: boolean;
    private storagePrefix = 'foodbook:cache:';

    constructor(options: PersistentCacheOptions = {}) {
        // L2 缓存：较大容量，较长 TTL
        this.cache = new LRUCache<string, CachedData<any>>({
            maxSize: 100,
            defaultTtl: TTL_PRESETS.MEDIUM
        });
        this.enabled = options.enabled ?? true;
        this.debug = options.debug ?? false;
    }

    /**
     * 获取完整存储键名
     */
    private getStorageKey(key: string): string {
        return `${this.storagePrefix}${key}`;
    }

    /**
     * 计算数据大小（字节）
     */
    private calculateSize(data: any): number {
        try {
            return new TextEncoder().encode(JSON.stringify(data)).length;
        } catch {
            return 0;
        }
    }

    /**
     * 日志输出
     */
    private log(...args: any[]): void {
        if (this.debug) {
            console.log('[PersistentCache]', ...args);
        }
    }

    /**
     * 检查缓存是否有效
     */
    isValid<T>(cached: CachedData<T> | null): boolean {
        if (!cached) return false;
        if (!this.enabled) return false;
        
        const now = Date.now();
        return cached.metadata.expiresAt > now;
    }

    /**
     * 获取缓存数据
     * @param key 缓存键
     * @param skipTtlCheck 是否跳过过期检查
     */
    get<T>(key: string, skipTtlCheck = false): T | null {
        if (!this.enabled) {
            this.log('Disabled, skipping get');
            return null;
        }

        // 先尝试内存缓存
        const cached = this.cache.get(key, skipTtlCheck) as CachedData<T> | null;
        
        if (cached) {
            if (skipTtlCheck || this.isValid(cached)) {
                this.log(`Cache hit: ${key}`);
                return cached.data;
            } else {
                this.log(`Cache expired: ${key}`);
                this.cache.delete(key);
            }
        }

        // 尝试从 LocalStorage 恢复
        try {
            const storageKey = this.getStorageKey(key);
            const stored = localStorage.getItem(storageKey);
            
            if (stored) {
                const parsed = JSON.parse(stored) as CachedData<T>;
                
                if (skipTtlCheck || this.isValid(parsed)) {
                    // 恢复到内存缓存
                    this.cache.set(key, parsed);
                    this.log(`Restored from localStorage: ${key}`);
                    return parsed.data;
                } else {
                    // 过期数据，清理
                    localStorage.removeItem(storageKey);
                    this.log(`Cleaned expired localStorage: ${key}`);
                }
            }
        } catch (error) {
            this.log(`Failed to read from localStorage: ${key}`, error);
            // LocalStorage 读取失败，静默处理
        }

        this.log(`Cache miss: ${key}`);
        return null;
    }

    /**
     * 设置缓存数据
     * @param key 缓存键
     * @param data 数据
     * @param ttl 过期时间 (ms)
     * @param version 数据版本（可选）
     */
    set<T>(key: string, data: T, ttl?: number, version?: string): void {
        if (!this.enabled) {
            this.log('Disabled, skipping set');
            return;
        }

        const now = Date.now();
        const effectiveTtl = ttl ?? this.cache['defaultTtl'];
        
        const cached: CachedData<T> = {
            metadata: {
                timestamp: now,
                expiresAt: now + effectiveTtl,
                version,
                size: this.calculateSize(data)
            },
            data
        };

        // 存入内存缓存
        this.cache.set(key, cached, ttl);
        this.log(`Cached in memory: ${key}, expires at ${new Date(cached.metadata.expiresAt).toISOString()}`);

        // 同步到 LocalStorage（公共数据或大 TTL 数据）
        if (ttl === undefined || ttl >= TTL_PRESETS.LONG) {
            const storageKey = this.getStorageKey(key);
            try {
                localStorage.setItem(storageKey, JSON.stringify(cached));
                this.log(`Synced to localStorage: ${key}`);
            } catch (error) {
                // LocalStorage 可能已满，静默失败
                this.log(`Failed to write to localStorage: ${key}`, error);
                
                // 尝试清理过期数据后重试
                if (error instanceof DOMException && error.name === 'QuotaExceededError') {
                    this.cleanup();
                    try {
                        localStorage.setItem(storageKey, JSON.stringify(cached));
                    } catch {
                        // 仍然失败则放弃
                    }
                }
            }
        }
    }

    /**
     * 删除缓存
     */
    delete(key: string): void {
        this.cache.delete(key);
        
        try {
            const storageKey = this.getStorageKey(key);
            localStorage.removeItem(storageKey);
        } catch (error) {
            this.log(`Failed to delete from localStorage: ${key}`, error);
        }
        
        this.log(`Deleted cache: ${key}`);
    }

    /**
     * 清空所有缓存
     */
    clear(): void {
        this.cache.clear();
        
        try {
            const keys: string[] = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(this.storagePrefix)) {
                    keys.push(key);
                }
            }
            keys.forEach(key => localStorage.removeItem(key));
        } catch (error) {
            this.log('Failed to clear localStorage', error);
        }
        
        this.log('Cleared all caches');
    }

    /**
     * 清理过期缓存
     * @returns 清理的数量
     */
    cleanup(): number {
        let count = 0;
        
        // 清理内存缓存
        count += this.cache.cleanup();
        
        // 清理 LocalStorage
        try {
            const keysToRemove: string[] = [];
            const now = Date.now();
            
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(this.storagePrefix)) {
                    try {
                        const stored = localStorage.getItem(key);
                        if (stored) {
                            const parsed = JSON.parse(stored) as CachedData<any>;
                            if (parsed.metadata.expiresAt < now) {
                                keysToRemove.push(key);
                            }
                        }
                    } catch {
                        // 解析失败，删除
                        keysToRemove.push(key);
                    }
                }
            }
            
            keysToRemove.forEach(key => {
                localStorage.removeItem(key);
                count++;
            });
        } catch (error) {
            this.log('Failed to cleanup localStorage', error);
        }
        
        this.log(`Cleaned up ${count} expired entries`);
        return count;
    }

    /**
     * 获取缓存统计信息
     */
    getStats(): {
        memoryCache: {
            size: number;
            hitRate: number;
        };
        localStorage: {
            entries: number;
            totalSize: number;
        };
    } {
        const memoryStats = this.cache.getStats();
        
        let localStorageEntries = 0;
        let localStorageSize = 0;
        
        try {
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(this.storagePrefix)) {
                    localStorageEntries++;
                    const value = localStorage.getItem(key);
                    if (value) {
                        localStorageSize += value.length;
                    }
                }
            }
        } catch {
            // 忽略错误
        }
        
        return {
            memoryCache: {
                size: memoryStats.size,
                hitRate: memoryStats.hitRate
            },
            localStorage: {
                entries: localStorageEntries,
                totalSize: localStorageSize
            }
        };
    }

    /**
     * 刷新缓存（更新过期时间）
     */
    refresh(key: string, ttl?: number): boolean {
        const cached = this.cache.get(key, true);
        if (cached) {
            this.cache.refresh(key, ttl);
            return true;
        }
        return false;
    }

    /**
     * 批量获取缓存
     */
    getMany<T>(keys: string[]): Map<string, T> {
        const results = new Map<string, T>();
        for (const key of keys) {
            const data = this.get<T>(key);
            if (data !== null) {
                results.set(key, data);
            }
        }
        return results;
    }

    /**
     * 批量设置缓存
     */
    setMany<T>(entries: Array<{ key: string; data: T; ttl?: number; version?: string }>): void {
        for (const { key, data, ttl, version } of entries) {
            this.set(key, data, ttl, version);
        }
    }

    /**
     * 导出所有缓存数据（用于备份）
     */
    export(): Array<{ key: string; data: any; metadata: CacheMetadata }> {
        return this.cache.export().map(entry => ({
            key: entry.key as string,
            data: entry.value.data,
            metadata: {
                timestamp: entry.value.metadata.timestamp,
                expiresAt: entry.value.metadata.expiresAt,
                version: entry.value.metadata.version,
                size: entry.value.metadata.size
            }
        }));
    }

    /**
     * 导入缓存数据（用于恢复）
     */
    import(data: Array<{ key: string; data: any; metadata: CacheMetadata }>): void {
        const now = Date.now();
        const validEntries = data.filter(entry => {
            const remainingTtl = entry.metadata.expiresAt - now;
            return remainingTtl > 0;
        });
        
        validEntries.forEach(entry => {
            const remainingTtl = entry.metadata.expiresAt - now;
            this.set(entry.key, entry.data, remainingTtl, entry.metadata.version);
        });
    }
}

/**
 * 预定义的缓存键
 */
export const CacheKeys = {
    // 公共数据
    PUBLIC_RECIPES: 'public:recipes',
    PUBLIC_INGREDIENTS: 'public:ingredients',
    
    // 用户数据（需要添加 userId 后缀）
    userProfile: (userId: string) => `user:${userId}:profile`,
    userSchedule: (userId: string) => `user:${userId}:schedule`,
    userShopping: (userId: string) => `user:${userId}:shopping`,
    userInventory: (userId: string) => `user:${userId}:inventory`,
    
    // 元数据
    CACHE_VERSION: 'meta:cache-version',
} as const;

// 导出单例
// 开发环境和生产环境都启用缓存，开发模式下会输出调试日志
const isDev = typeof import.meta !== 'undefined' && import.meta.env?.DEV === true;
export const persistentCache = new PersistentCacheService({
    enabled: true,  // 始终启用缓存
    debug: isDev    // 仅开发模式输出详细日志
});
